<template>
    <aside class="mc-sidebar-customer" data-testid="customer-sidebar">
        <div class="mc-sidebar-customer__wrapper">
            <!-- Form Customer / Company -->
            <div class="mc-customer-form">
                <h5 class="mc-customer-form__title">📍 Lokasi Customer</h5>

                <!-- Company Selection (Multiselect with backend search) -->
                <div class="mb-2">
                    <label class="form-label-md">Pilih Perusahaan</label>
                    <multiselect v-model="selectedCompany" :options="companies" :loading="isLoadingCompanies"
                        track-by="id" label="name" placeholder="Cari atau pilih perusahaan..." :searchable="true"
                        :internal-search="false" :allow-empty="false" @search-change="onSearchChange"
                        @select="onCompanySelect">
                        <!-- Slot untuk menampilkan opsi di dropdown -->
                        <template slot="option" slot-scope="props">
                            <span>{{ props.option.name }}</span>
                            <small v-if="props.option.domain_name" class="text-muted ml-1">
                                ({{ props.option.domain_name }})
                            </small>
                        </template>

                        <!-- Slot untuk menampilkan pilihan yang sudah dipilih -->
                        <template slot="singleLabel" slot-scope="props">
                            <span>{{ props.option.name }}</span>
                            <small v-if="props.option.domain_name" class="text-muted ml-1">
                                ({{ props.option.domain_name }})
                            </small>
                        </template>

                        <!-- Slots lain opsional -->
                        <template slot="noResult">
                            Tidak ada perusahaan ditemukan
                        </template>
                        <template slot="noOptions">
                            Ketik untuk mencari perusahaan
                        </template>
                    </multiselect>
                </div>

                <!-- Auto-filled fields (readonly) -->
                <div class="mb-2">
                    <label class="form-label-md">Nama Customer</label>
                    <input v-model="customer.name" type="text" class="form-control form-control-md"
                        placeholder="Nama perusahaan / customer" readonly disabled>
                </div>

                <!-- ===== BAGIAN YANG BISA DI-COLLAPSE ===== -->
                <transition name="collapse">

                    <div id="collapsible-detail" v-show="!isCollapsed" class="mc-collapsible-body">
                        <!-- Alamat -->
                        <div class="mb-2">
                            <label class="form-label-md">Alamat</label>
                            <AddressSearch v-model="customer.address" @select="onAddressSelected" />
                        </div>

                        <small class="text-muted d-block mt-2 mb-2" style="font-size: 0.7rem;">
                            <i class="fas fa-info-circle"></i>
                            Jika lokasi belum tepat, ubah Latitude/Longitude lalu klik "Konfirmasi Alamat" untuk
                            memperbarui
                            alamat.
                        </small>

                        <!-- Latitude & Longitude -->
                        <div class="row g-2 mb-3">
                            <div class="col-6">
                                <label class="form-label-md">Latitude</label>
                                <input v-model.number="customer.lat" type="number" step="any"
                                    class="form-control form-control-md" placeholder="Contoh: -6.2088"
                                    @paste="onPasteCoordinates">
                            </div>
                            <div class="col-6">
                                <label class="form-label-md">Longitude</label>
                                <input v-model.number="customer.lng" type="number" step="any"
                                    class="form-control form-control-md" placeholder="Contoh: 106.8456"
                                    @paste="onPasteCoordinates">
                            </div>
                        </div>

                        <!-- Tombol Konfirmasi Alamat -->
                        <div class="mb-3">
                            <button type="button" class="btn btn-sm btn-outline-primary w-100" @click="confirmAddress"
                                :disabled="!hasValidCustomerCoords">
                                <i class="fas fa-map-marker-alt me-1"></i> Konfirmasi Alamat
                            </button>
                        </div>

                        <!-- Tombol Find nearest -->
                        <button class="mc-btn-find" @click="findNearest"
                            :disabled="!hasValidCustomerCoords || isLoading">
                            <i class="fas fa-search"></i> {{ isLoading ? 'Mencari...' : 'Find nearest' }}
                        </button>
                    </div>
                </transition>
                <!-- ===== AKHIR BAGIAN COLLAPSIBLE ===== -->
                <div v-if="searchResults.length" class="mc-toggle-icon-wrapper">
                    <i class="fas" :class="isCollapsed ? 'fa-angle-double-down' : 'fa-angle-double-up'"
                        @click="toggleCollapse" :title="isCollapsed ? 'Buka detail lokasi' : 'Tutup detail lokasi'"
                        role="button" tabindex="0" aria-expanded="!isCollapsed" aria-controls="collapsible-detail"
                        @keydown.enter="toggleCollapse"></i>
                </div>
            </div>

            <!-- Hasil Pencarian -->
            <div class="mc-results-section" v-if="searchResults.length || isLoading">
                <div class="mc-results-header">
                    <h6 class="mc-results-title">
                        RANKED BY COVERAGE + DISTANCE
                        <span class="mc-results-count">({{ searchResults.length }} teknisi)</span>
                    </h6>
                </div>
                <div class="mc-results-list" v-if="!isLoading">
                    <div v-for="(tech, idx) in searchResults" :key="tech.user_id" class="mc-result-card"
                        :class="tech.is_coverage ? 'in-coverage' : 'out-of-zone'" @click="selectTechnician(tech)">
                        <div class="mc-result-rank">#{{ idx + 1 }}</div>
                        <div class="mc-result-body">
                            <div class="mc-result-name">{{ tech.name || 'Tanpa Nama' }}</div>
                            <div class="mc-result-address">
                                <i class="fas fa-map-marker-alt"></i>
                                {{ tech.address?.address || 'Alamat tidak tersedia' }}
                            </div>
                            <div class="mc-result-stats">
                                <span class="mc-distance"><i class="fas fa-route"></i> {{ tech.distance_km?.toFixed(1)
                                    ?? '?' }} km</span>
                                <span class="mc-coverage-badge" :class="tech.is_coverage ? 'in' : 'out'">
                                    {{ tech.is_coverage ? 'In coverage' : 'Out of zone' }}
                                </span>
                                <span class="mc-status-badge" :class="tech.status === 'A' ? 'available' : 'busy'">
                                    {{ tech.status === 'A' ? 'Available' : 'Busy' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="mc-loading-state">
                    <b-spinner small variant="primary" /> Menghitung jarak...
                </div>
            </div>
            <div v-else-if="!isLoading && hasSearched" class="mc-empty-state">
                <i class="fas fa-user-slash"></i>
                <span>Tidak ada teknisi ditemukan</span>
                <small>Ubah alamat customer atau coba lagi</small>
            </div>
            <div v-else class="mc-empty-state mc-empty-state--prompt">
                <i class="fas fa-location-dot"></i>
                <span>Masukkan alamat customer dan klik <strong>Find nearest</strong></span>
            </div>
        </div>
    </aside>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Helper } from '@/mixin/helper'
import * as usersApi from '@/services/rest-api-sms/lib/users/users'
import * as companyApi from '@/services/rest-api-sms/lib/company/company' // sesuaikan path
import Multiselect from 'vue-multiselect'
import AddressSearch from './AddressSearch.vue';

export default {
    name: 'SideBar',
    mixins: [Helper],
    components: {
        Multiselect,
        AddressSearch,
    },
    data() {
        return {
            customer: {
                name: '',
                address: '',
                lat: null,
                lng: null,
            },
            isLoading: false,
            searchResults: [],
            hasSearched: false,
            // Data perusahaan
            companies: [],
            selectedCompany: null,
            isLoadingCompanies: false,
            searchQuery: '',
            fieldErrors: {
                lat: '',
                lng: '',
                address: ''
            },
            isCollapsed: false, // state collapse untuk bagian bawah
        }
    },

    computed: {
        ...mapState('users', ['technicians']),
        hasValidCustomerCoords() {
            return this.customer.lat != null && this.customer.lng != null &&
                !isNaN(parseFloat(this.customer.lat)) && !isNaN(parseFloat(this.customer.lng))
        },
    },

    watch: {
        'customer.lat': {
            handler() { this.updateCustomerLocation(); },
            deep: true
        },
        'customer.lng': {
            handler() { this.updateCustomerLocation(); },
            deep: true
        },
        '$store.state.map.customerLocation': {
            handler(newLoc) {
                if (!newLoc) return;
                const newLat = newLoc.lat;
                const newLng = newLoc.lng;
                if (typeof newLat === 'number' && typeof newLng === 'number') {
                    const curLat = this.customer.lat;
                    const curLng = this.customer.lng;
                    const needUpdateCoords = Math.abs(curLat - newLat) > 0.000001 || Math.abs(curLng - newLng) > 0.000001;
                    if (needUpdateCoords) {
                        this.customer.lat = newLat;
                        this.customer.lng = newLng;
                    }
                    // Update alamat jika ada perubahan
                    if (newLoc.address !== undefined && this.customer.address !== newLoc.address) {
                        this.customer.address = newLoc.address;
                        this.clearFieldError('address');
                    }
                }
            },
            deep: true
        }
    },

    methods: {
        toggleCollapse() {
            this.isCollapsed = !this.isCollapsed;
        },

        onPasteCoordinates(event) {
            // Ambil teks yang akan ditempel
            const pasted = (event.clipboardData || window.clipboardData).getData('text');
            if (!pasted) return;

            // Coba pecah berdasarkan koma
            const parts = pasted.split(', ').map(s => s.trim());
            if (parts.length === 2) {
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    // Cegah paste default (agar tidak masuk ke satu field)
                    event.preventDefault();

                    // Isi kedua field
                    this.customer.lat = lat;
                    this.customer.lng = lng;

                    // Bersihkan error (jika ada)
                    this.clearFieldError('lat');
                    this.clearFieldError('lng');

                    // Update lokasi di store & map (jika diperlukan)
                    this.updateCustomerLocation();

                    // (Opsional) Tampilkan notifikasi
                    this.$toast?.success('Koordinat berhasil dipisahkan', { timeout: 1500 });
                }
            }
            // Jika bukan format dua angka, biarkan paste normal (tidak dicegah)
        },

        ...mapActions('users', ['loadTechnicians']),

        onAddressSelected(payload) {
            if (!payload || typeof payload.lat === 'undefined' || typeof payload.lng === 'undefined') {
                this.$toast?.error('Data alamat tidak lengkap');
                return;
            }
            const lat = Number(payload.lat);
            const lng = Number(payload.lng);
            if (isNaN(lat) || isNaN(lng)) {
                this.$toast?.error('Koordinat tidak valid');
                return;
            }
            // 1. Matikan auto-center terlebih dahulu
            this.$store.commit('map/SET_DISABLE_AUTO_CENTER', true);

            // 2. Null-kan selected technician
            this.$store.commit('map/SET_SELECTED_TECHNICIAN', null);

            // 3. Set koordinat customer
            this.customer.lat = Number(payload.lat);
            this.customer.lng = Number(payload.lng);
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat: this.customer.lat, lng: this.customer.lng });

            // 4. Zoom ke customer (akan panggil centerToCustomerOnly)
            this.$nextTick(() => {
                // Beri sedikit delay untuk memastikan map listener sudah terpasang
                setTimeout(() => {
                    this.centerToCustomer();
                }, 100);
            });
        },

        clearFieldError(field) {
            if (this.fieldErrors && this.fieldErrors[field] !== undefined) {
                this.fieldErrors[field] = '';
            }
        },

        updateCustomerLocation() {
            if (this.hasValidCustomerCoords) {
                const current = this.$store.state.map.customerLocation;
                // Hanya commit jika koordinat benar-benar berubah
                if (current &&
                    Math.abs(current.lat - this.customer.lat) < 1e-8 &&
                    Math.abs(current.lng - this.customer.lng) < 1e-8) {
                    return; // Tidak perlu update
                }
                this.$store.commit('map/SET_CUSTOMER_LOCATION', {
                    lat: this.customer.lat,
                    lng: this.customer.lng,
                    address: this.customer.address,
                    name: this.customer.name
                });
            } else {
                this.$store.commit('map/SET_CUSTOMER_LOCATION', null);
            }
        },

        // --- Load daftar perusahaan dari API dengan dua tahap (limit=1 lalu limit=total_count) ---
        async loadCompanies(search = '') {
            this.isLoadingCompanies = true
            try {
                // Tahap 1: ambil total_count (dengan limit=1)
                const paramsFirst = { limit: 10, page: 1, filters: [`is_all_server eq true`] }
                if (search) paramsFirst.search = search
                const firstRes = await companyApi.get(paramsFirst)
                const paginatedResult = firstRes?.data?.paginated_result || []
                const totalCount = firstRes?.metadata?.total_count || paginatedResult.length

                if (totalCount === 0) {
                    this.companies = []
                    return
                }

                // Tahap 2: ambil semua data
                const paramsSecond = { limit: totalCount, page: 1, filters: [`is_all_server eq true`] }
                if (search) paramsSecond.search = search
                const secondRes = await companyApi.get(paramsSecond)
                // Ambil array dari data.paginated_result
                const companiesData = secondRes?.data?.paginated_result || []
                if (!Array.isArray(companiesData)) {
                    this.companies = []
                    return
                }
                // Mapping ke format yang dipakai multiselect (id, name, address, lat, lng)
                // Karena data tidak punya address/lat/lng, isi default kosong atau bisa ditambahkan nanti
                this.companies = companiesData.map(comp => ({
                    id: comp.company_id,
                    name: comp.name,
                    domain_name: comp.domain_name,
                    server_region: comp.server_region,
                    address: '',    // tidak tersedia dari API
                    lat: null,      // tidak tersedia
                    lng: null       // tidak tersedia
                }))
            } catch (err) {
                console.error('Gagal memuat daftar perusahaan:', err)
                this.$toast?.error('Gagal memuat daftar perusahaan')
                this.companies = []
            } finally {
                this.isLoadingCompanies = false
            }
        },

        // --- Event ketika user mengetik pencarian perusahaan ---
        onSearchChange(query) {
            this.searchQuery = query
            this.loadCompanies(query)
        },

        // +++ Method baru untuk mengambil detail perusahaan by ID +++
        async fetchCompanyDetailById(companyId, serverRegion) {
            try {
                const response = await companyApi.getById(companyId, serverRegion)
                // Sesuaikan dengan struktur response API
                const companyData = response?.data || response
                if (companyData) {
                    return {
                        name: companyData.name,
                        address: companyData.address || '',
                        lat: companyData.latitude || companyData.lat || null,
                        lng: companyData.longitude || companyData.lng || null
                    }
                }
                return null
            } catch (err) {
                console.error('Gagal mengambil detail perusahaan:', err)
                this.$toast?.error('Gagal mengambil detail perusahaan')
                return null
            }
        },

        // Ubah onCompanySelect
        async onCompanySelect(selected) {
            if (!selected) return;
            // Reset data customer
            this.customer = { name: '', address: '', lat: null, lng: null };
            this.searchResults = [];
            this.hasSearched = false;

            this.$store.commit('map/SET_DISABLE_AUTO_CENTER', true);
            this.$store.commit('map/SET_SELECTED_TECHNICIAN', null);

            // Reset data customer
            this.customer = {
                name: '',
                address: '',
                lat: null,
                lng: null,
            }
            this.searchResults = []
            this.hasSearched = false

            // Buka kembali form bagian bawah
            this.isCollapsed = false; // <--- tambahkan baris ini

            // Ambil detail lengkap perusahaan berdasarkan ID
            const detail = await this.fetchCompanyDetailById(selected.id, selected.server_region)

            if (detail && detail.name) {
                this.customer.name = detail.name
                this.customer.address = detail.address || ''
                this.customer.lat = detail.lat
                this.customer.lng = detail.lng

                // Jika dari API sudah punya alamat tapi belum ada koordinat, lakukan geocoding
                if (this.customer.address && (!this.customer.lat || !this.customer.lng)) {
                    await this.geocodeAddress()
                }
                // Jika sama sekali tidak punya alamat, coba cari berdasarkan nama
                else if (!this.customer.address && this.customer.name) {
                    await this.geocodeCompanyName(this.customer.name)
                }
            } else {
                // Fallback ke data awal dari multiselect
                this.customer.name = selected.name
                if (selected.name) {
                    await this.geocodeCompanyName(selected.name)
                }
            }

            this.$toast?.success(`Perusahaan "${this.customer.name}" dipilih`, { timeout: 1500 })
        },

        async geocodeCompanyName(companyName) {
            if (!companyName) return
            try {
                const response = await fetch(
                    `${process.env.VUE_APP_MAP_URL}/search?format=json&q=${encodeURIComponent(companyName)}&limit=1`
                )
                const data = await response.json()
                if (data && data.length) {
                    this.customer.address = data[0].display_name
                    this.customer.lat = parseFloat(data[0].lat)
                    this.customer.lng = parseFloat(data[0].lon)
                    this.$toast?.success('Alamat dan koordinat ditemukan otomatis')
                } else {
                    this.$toast?.warning('Tidak dapat menemukan alamat untuk perusahaan ini, silakan isi manual')
                }
            } catch (err) {
                console.error(err)
                this.$toast?.error('Gagal mengambil koordinat')
            }
        },

        // --- Geocoding alamat customer (OpenStreetMap Nominatim) ---
        async geocodeAddress() {
            if (!this.customer.address || !this.customer.address.trim()) {
                this.$toast?.warn('Masukkan alamat customer terlebih dahulu')
                return
            }
            this.$toast?.info('Mencari koordinat alamat...', { timeout: 1000 })
            try {
                const response = await fetch(
                    `${process.env.VUE_APP_MAP_URL}/search?format=json&q=${encodeURIComponent(this.customer.address)}&limit=1`
                )
                const data = await response.json()
                if (data && data.length) {
                    this.customer.lat = parseFloat(data[0].lat)
                    this.customer.lng = parseFloat(data[0].lon)
                    if (!this.customer.name) {
                        this.customer.name = data[0].display_name?.split(',')[0] || 'Customer'
                    }
                    this.$toast?.success('Koordinat ditemukan')
                } else {
                    this.$toast?.error('Alamat tidak ditemukan')
                }
            } catch (err) {
                console.error(err)
                this.$toast?.error('Gagal mengambil koordinat')
            }
        },

        centerToCustomer() {
            if (this.hasValidCustomerCoords) {
                this.$root.$emit('map-center-to-customer', this.customer.lat, this.customer.lng, 16);
            }
        },

        async findNearest() {
            if (!this.hasValidCustomerCoords) {
                this.$toast?.error('Lengkapi latitude & longitude customer terlebih dahulu');
                return;
            }

            this.isLoading = true;
            this.hasSearched = true;
            this.searchResults = [];

            try {
                const params = {
                    filters: [
                        `lat eq ${this.customer.lat}`,
                        `lon eq ${this.customer.lng}`,
                    ],
                };

                const response = await usersApi.getTechnicians(params);
                const technicians = response?.data || response || [];

                // Simpan langsung ke store tanpa normalisasi
                this.$store.commit('users/SET_TECHNICIANS', technicians);

                // Mapping untuk tampilan sidebar, ambil field yang dibutuhkan
                this.searchResults = technicians.map(tech => {
                    // Ambil koordinat dari root atau dari address (sesuai struktur asli)
                    const lat = tech.latitude ?? tech.lat ?? tech.address?.latitude ?? tech.address?.lat;
                    const lng = tech.longitude ?? tech.lng ?? tech.address?.longitude ?? tech.address?.lng;

                    return {
                        user_id: tech.user_id,
                        name: tech.name,
                        email: tech.email,
                        phone_no: tech.phone_no,
                        latitude: parseFloat(lat) || null,
                        longitude: parseFloat(lng) || null,
                        address: tech.address,      // biarkan sesuai asli
                        location: tech.location,
                        distance_km: tech.distance_km,
                        duration_minute: tech.duration_minute,
                        is_coverage: tech.is_coverage,
                        status: tech.status || 'AVAILABLE',
                        original: tech,
                    };
                });

                if (this.searchResults.length > 0) {
                    this.isCollapsed = true; // 👈 collapse form bagian bawah
                }

                if (this.searchResults.length === 0) {
                    this.$toast?.info('Tidak ada teknisi ditemukan di sekitar lokasi');
                }
            } catch (err) {
                console.error('Gagal memanggil API nearest technicians:', err);
                this.$toast?.error('Terjadi kesalahan saat mencari teknisi');
                this.searchResults = [];
            } finally {
                this.isLoading = false;
            }
        },

        selectTechnician(tech) {
            if (!tech) return;

            this.$store.commit('map/SET_DISABLE_AUTO_CENTER', false);

            const fullTech = this.$store.state.users.technicians.find(t => t.user_id === tech.user_id);
            const dataToCommit = fullTech || tech;

            // Bentuk objek yang dikirim ke map tanpa mengubah data asli
            const mapData = {
                id: dataToCommit.user_id,
                user_id: dataToCommit.user_id,
                name: dataToCommit.name,
                area: dataToCommit.area ?? dataToCommit.address?.area,
                address: dataToCommit.address,   // asli
                latitude: dataToCommit.latitude ?? dataToCommit.address?.latitude,
                longitude: dataToCommit.longitude ?? dataToCommit.address?.longitude,
                radius: dataToCommit.radius ?? dataToCommit.address?.radius,
                email: dataToCommit.email,
            };

            this.$store.commit('map/SET_SELECTED_TECHNICIAN', mapData);

            if (mapData.latitude && mapData.longitude) {
                this.$root.$emit('map-fly-to-pin', mapData.latitude, mapData.longitude, 16);
            }
        },

        // Di dalam methods tambahkan:
        async confirmAddress() {
            if (!this.hasValidCustomerCoords) {
                this.$toast?.error('Lengkapi latitude & longitude customer terlebih dahulu');
                return;
            }

            this.$toast?.info('Mencari alamat berdasarkan koordinat...', { timeout: 1000 });

            try {
                const response = await fetch(
                    `${process.env.VUE_APP_MAP_URL}/reverse?format=json&lat=${this.customer.lat}&lon=${this.customer.lng}&accept-language=id`
                );
                const data = await response.json();

                if (data && data.display_name) {
                    this.customer.address = data.display_name;
                    this.clearFieldError('address');
                    this.$toast?.success('Alamat berhasil ditemukan');
                } else {
                    this.$toast?.warning('Alamat tidak ditemukan untuk koordinat ini');
                }
            } catch (err) {
                console.error('Reverse geocoding error:', err);
                this.$toast?.error('Gagal mendapatkan alamat: ' + (err.message || 'Error jaringan'));
            }
        },
    },
    mounted() {
        // Load daftar perusahaan awal tanpa pencarian
        this.loadCompanies()
    }
}
</script>

<style scoped>
/* ===================================
   Container Sidebar baru
=================================== */
.mc-sidebar-customer {
    position: relative;
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    overflow: hidden;
    font-size: 0.8125rem;
}

.mc-sidebar-customer__wrapper {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 16px;
    gap: 20px;
}

/* Form Customer */
.mc-customer-form {
    background: #f8fafc;
    border-radius: 16px;
    padding: 16px;
    border: 1px solid #eef2f7;
}

.mc-customer-form__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 12px;
    color: #0f172a;
}

.collapse-enter-active,
.collapse-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.collapse-enter,
.collapse-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    margin-top: 0;
    margin-bottom: 0;
}

.collapse-enter-to,
.collapse-leave {
    opacity: 1;
    max-height: 800px;
    /* cukup besar untuk menampung semua konten */
}

/* Pembungkus tombol – di tengah */
.mc-toggle-wrapper {
    text-align: center;
    margin: 6px 0 10px 0;
}

/* Tombol toggle */
.btn-toggle-collapse {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 6px 18px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.btn-toggle-collapse:hover {
    background: #e2e8f0;
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.btn-toggle-collapse:active {
    background: #d1d9e6;
}

.btn-toggle-collapse i {
    font-size: 0.7rem;
}

/* Pembungkus icon toggle - di tengah */
.mc-toggle-icon-wrapper {
    text-align: center;
    margin: 8px 0 0 0;
    padding: 4px 0;
}

/* Icon toggle */
.mc-toggle-icon-wrapper i {
    font-size: 1.2rem;
    color: #94a3b8;
    cursor: pointer;
    transition: color 0.2s, transform 0.2s;
    display: inline-block;
    padding: 4px 8px;
    border-radius: 6px;
}

.mc-toggle-icon-wrapper i:hover {
    color: #2563eb;
    background: #f1f5f9;
}

.mc-toggle-icon-wrapper i:active {
    transform: scale(0.9);
}

.form-label-md {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 4px;
    display: block;
}

/* Multiselect custom styling */
.multiselect {
    font-size: 0.8125rem;
}

.multiselect__tags {
    border-radius: 8px;
    border-color: #e2e8f0;
    min-height: 36px;
}

.multiselect__single {
    font-size: 0.8125rem;
}

.multiselect__placeholder {
    color: #94a3b8;
}

.mc-btn-find {
    width: 100%;
    background: #2563eb;
    border: none;
    padding: 8px 12px;
    border-radius: 12px;
    font-weight: 600;
    color: white;
    transition: background 0.2s;
    margin-top: 8px;
}

.mc-btn-find:hover:not(:disabled) {
    background: #1d4ed8;
}

.mc-btn-find:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
}

/* Hasil Section */
.mc-results-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.mc-results-header {
    padding-bottom: 6px;
    border-bottom: 2px solid #eef2f7;
}

.mc-results-title {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #475569;
    margin: 0;
}

.mc-results-count {
    font-weight: normal;
    margin-left: 6px;
    color: #64748b;
}

.mc-results-list {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-right: 4px;
}

/* Card hasil */
.mc-result-card {
    display: flex;
    gap: 12px;
    background: white;
    border-radius: 14px;
    padding: 12px;
    border: 1px solid #eef2f7;
    transition: all 0.2s ease;
    cursor: pointer;
}

.mc-result-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    transform: translateY(-1px);
}

.mc-result-card.in-coverage {
    border-left: 4px solid #16a34a;
}

.mc-result-card.out-of-zone {
    border-left: 4px solid #f97316;
}

.mc-result-rank {
    font-weight: 700;
    font-size: 1.2rem;
    color: #94a3b8;
    min-width: 32px;
    text-align: center;
}

.mc-result-body {
    flex: 1;
}

.mc-result-name {
    font-weight: 700;
    font-size: 0.9rem;
    color: #0f172a;
    margin-bottom: 4px;
}

.mc-result-address {
    font-size: 0.7rem;
    color: #64748b;
    margin-bottom: 8px;
    display: flex;
    gap: 6px;
    align-items: baseline;
}

.mc-result-stats {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    font-size: 0.7rem;
    font-weight: 600;
}

.mc-distance {
    color: #334155;
}

.mc-coverage-badge {
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 600;
}

.mc-coverage-badge.in {
    background: #dcfce7;
    color: #15803d;
}

.mc-coverage-badge.out {
    background: #ffedd5;
    color: #c2410c;
}

.mc-status-badge {
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 600;
}

.mc-status-badge.available {
    background: #e0f2fe;
    color: #0369a1;
}

.mc-status-badge.busy {
    background: #fee2e2;
    color: #b91c1c;
}

/* Empty states */
.mc-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 32px 16px;
    color: #64748b;
    gap: 8px;
}

.mc-empty-state i {
    font-size: 2rem;
    opacity: 0.5;
}

.mc-loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: #475569;
}

/* Responsive */
@media (max-width: 768px) {
    .mc-sidebar-customer {
        width: 85vw;
    }
}
</style>
